import { Box, IconButton } from '@material-ui/core';
import { getUsersByUserIds, UserType } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { LoadingValuesWarning } from '@/components/modules/Documentation/components/shared/LoadingValuesWarning';
import { Icon } from '@/components/shared/components/Icon';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { UserActionsTableDialog } from '@/components/shared/components/UserList/components/UserActionsTableDialog/UserActionsTableDialog';
import { UserTable } from '@/components/shared/components/UserList/components/UserTable/UserTable';
import { SPY_USER_EMAIL } from '@/components/shared/components/UserList/constants/SpyUserEmail';
import { UserListTableProps } from '@/components/shared/components/UserList/types/TableProps';
import { UserTableActionType } from '@/components/shared/components/UserList/types/UserTableActionType';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';
import { useUser } from '@/hooks/useUser';

interface UserListProps extends UserListTableProps {
  userType: UserType;
  editModeAllowed: boolean;
  userIds: string[];
  hideSubsectionWrapper?: boolean;
  subsectionTitle?: string;
}

export const REVALIDATE_USERS_LIST_ID = 'revalidate-credentials-list';

export const UserList = ({
  userIds,
  userType,
  editModeAllowed,
  subsectionTitle,
  hideSubsectionWrapper = false,
  tableProps,
}: UserListProps) => {
  const { userEntity } = useUser();
  const [userAction, setUserAction] = useState<UserTableActionType | undefined>(undefined);

  const { opened, toggleDialog, setOpened } = useDialog();

  const {
    data: users,
    loading,
    revalidate,
    error,
  } = useCache(getUsersByUserIds, { query: `&users[]=${userIds.join('&users[]=')}` });

  if (loading) {
    return (
      <UsersListWrapper
        userType={userType}
        revalidate={revalidate}
        hideSubsectionWrapper={hideSubsectionWrapper}
        subsectionTitle={subsectionTitle}
      >
        <LoadingValuesWarning
          boxProps={{ height: '66px', padding: '26px', width: '100%', justifyContent: 'center !important' }}
        />
      </UsersListWrapper>
    );
  }

  const toggleUserActionsDialog = (userActionType: UserTableActionType) => {
    if (!editModeAllowed) return;
    setUserAction(userActionType);
    toggleDialog();
  };

  const isAnyUserUnblocked = users?.some((user) => user.isBlocked === false);

  const isSpied = users?.some((user) => user.username === SPY_USER_EMAIL);

  const isUserMCA = userEntity?.isRoleMerchantChannelOrAgent();

  let actions: ActionsOption[] | undefined;

  if (editModeAllowed) {
    actions = !isUserMCA
      ? [
          {
            display: <Text tId={'ADD_USER'} />,
            onClick: () => toggleUserActionsDialog(UserTableActionType.ADD_USER),
            iconName: 'add',
          },
        ]
      : [];

    if (userType === UserType.AGENT && userIds.length === 1) {
      actions.push({
        display: <Text tId={`SPY_USER_${userType}`} />,
        iconName: 'bug_report',
        disabled: isSpied === true,
        onClick: () => toggleUserActionsDialog(UserTableActionType.SPY_USER),
      });
    }

    if (isAnyUserUnblocked && !isUserMCA) {
      actions.push({
        display: <Text tId={'BLOCK_ALL_USERS'} />,
        onClick: () => toggleUserActionsDialog(UserTableActionType.BLOCK_ALL_USERS),
        iconName: 'lock',
      });
    }
  }

  return (
    <UsersListWrapper
      subsectionTitle={subsectionTitle}
      revalidate={revalidate}
      hideSubsectionWrapper={hideSubsectionWrapper}
      userType={userType}
      actions={actions}
    >
      <>
        {users && !error ? (
          <Box mt={2}>
            <UserTable users={users} userId={users[0].id} editModeAllowed={editModeAllowed} tableProps={tableProps} />
          </Box>
        ) : (
          <PendingDataWarning type={PendingDataType.CREDENTIALS} />
        )}
        {userAction && (
          <UserActionsTableDialog
            userType={userType}
            userId={userIds[0]}
            users={users}
            userAction={userAction}
            opened={opened}
            setOpened={setOpened}
            toggleDialog={toggleDialog}
            revalidate={revalidate}
          />
        )}
      </>
    </UsersListWrapper>
  );
};

const UsersListWrapper = ({
  children,
  revalidate,
  userType,
  hideSubsectionWrapper,
  subsectionTitle,
  actions,
}: {
  children: React.ReactNode;
  revalidate: () => void;
  userType: UserType;
  hideSubsectionWrapper?: boolean;
  subsectionTitle?: string;
  actions?: ActionsOption[];
}) => (
  <SubSectionWrapper
    title={hideSubsectionWrapper ? undefined : subsectionTitle ?? `USER_CREDENTIALS_${userType}`}
    specificActionsOptionsArray={hideSubsectionWrapper ? undefined : actions}
    rightComponent={
      <IconButton id={REVALIDATE_USERS_LIST_ID} onClick={() => revalidate()}>
        <Icon size={20}>refresh</Icon>
      </IconButton>
    }
  >
    {children}
  </SubSectionWrapper>
);
